import * as React from "react";
import * as ReactDOM from "react-dom";
import RandomColor from "../../utils/randomColor";
import { IColor } from "../../models";
import { LABEL_COLORS, LABEL_PATTERNS, CALENDAR_COLORS } from "../../utils/constants";
import { InputBase, InputContainer, InputHint, InputError, IInputBaseProperties } from "../input";
import { PopupProvider, PopupTrigger, Popup, PopupAlign } from "../popup";


interface IProperties extends IInputBaseProperties
{
    /**
     * Inline-Style.
     */
    style?: React.CSSProperties,

    /**
     * Anzeigetext der Checkbox.
     */
    label?: string,

    /**
     * Die momentane selektierte Farbe des ColorPickers. 
     */
    color?: IColor | string,

    /**
     * Die Farben die zur Auswahl stehen sollen. 
     */
    colors: "labels" | "displaytypes" | "calendars" | "status",

    /**
     * Setzt einen Hilfetext.
     */
    hint?: string | JSX.Element,

    /**
     * Wird ausgeführt, sobald auf eine Farbe geklickt wird.
     */
    onColorClick?: (color: IColor) => void
}

interface IState
{
    color: string
}

export default class ColorPicker extends React.Component<IProperties, IState>
{
    private _statusColors: IColor[] = [];

    constructor(props: IProperties)
    {
        super(props);

        // Farben für Statuseinträge generieren.
        var randomColor = new RandomColor();
        var statusColors = randomColor.generate({
            luminosity: "light",
            count: 50,
            seed: "9005524408327168220"
        });

        statusColors.map((color, index) => this._statusColors.push({
            name: index.toString(),
            color: color
        }));

        var color = this.getColor(props.color);
        this.state = {
            color: color ? color.color : null
        };
    }

    componentWillReceiveProps(nextProps: IProperties)
    {
        const { color } = this.props;

        if (typeof color === "string")
        {
            if (color !== nextProps.color)
            {
                this.setState({
                    color: (nextProps.color as string)
                });
            }
        }
        else
        {
            if (color && color.name !== (nextProps.color as IColor).name)
            {
                this.setState({
                    color: (nextProps.color as IColor).color
                });
            }
        }
    }

    /**
     * Gibt eine <label /> Element zurück. Falls nötig.
     */
    private getLabel()
    {
        if (this.props.label != null)
        {
            return <label htmlFor={this.props.id}>{this.props.label}</label>
        }
    }

    /**
     * Ermittelt die selektierte Farbe.
     */
    private getColor(color: IColor | string): IColor
    {
        const { colors } = this.props;
        var clr: string = null;

        if (typeof color === "string")
        {
            clr = color;
        }
        else
        {
            if (color != null && color.color != null)
            {
                clr = color.color;
            }
        }

        if (color == null)
        {
            if (colors === "labels")
            {
                return LABEL_COLORS.where(c => c.name === "null").first();
            }
            else if (colors === "displaytypes")
            {
                return LABEL_PATTERNS.where(c => c.name === "pattern1").first();
            }
            else if (colors === "calendars")
            {
                return CALENDAR_COLORS.where(c => c.name === "null").first();
            }
            else if (colors === "status")
            {
                return this._statusColors.first();
            }
        }

        if (colors === "labels")
        {
            return LABEL_COLORS.where(c => c.color === clr).first();
        }
        else if (colors === "displaytypes")
        {
            return LABEL_PATTERNS.where(c => c.color === clr).first();
        }
        else if (colors === "calendars")
        {
            return CALENDAR_COLORS.where(c => c.color === clr).first();
        }
        else if (colors === "status")
        {
            return this._statusColors.where(c => c.color === clr).first();
        }
    }

    /**
     * Wird ausgeführt, sobald auf eine Farbe im Popup geklickt wird.
     * @param color
     */
    private onColorClick(color: IColor)
    {
        this.props.onColorClick && this.props.onColorClick(color);
    }

    /**
     * Rendert das Popup mit den Farben die zur Auswahl stehen.
     */
    private renderColorPopup(): JSX.Element
    {
        var { style, colors, onColorClick } = this.props;
        var { color } = this.state;

        if (colors === "labels")
        {
            return (
                <div className="po-colorpicker">
                    {
                        LABEL_COLORS.map(c =>
                        {
                            return (
                                <div
                                    className="po-colorpicker_color"
                                    style={{
                                        backgroundColor: c.color
                                    }}
                                    onClick={() => this.onColorClick(c)}>
                                    {
                                        c.color === color &&
                                        <div className="po-colorpicker_selected">
                                            <i className="mdi mdi-check"></i>
                                        </div>
                                    }
                                </div>
                            );
                        })
                    }
                </div>
            );
        }
        else if (colors === "displaytypes")
        {
            return (
                <div className="po-colorpicker">
                    {
                        LABEL_PATTERNS.map(c =>
                        {
                            return (
                                <div
                                    className={`po-colorpicker_color po-colorpicker_color--${c.color}`}
                                    onClick={() => this.onColorClick(c)}>
                                    {
                                        c.color === color &&
                                        <div className="po-colorpicker_selected">
                                            <i className="mdi mdi-check"></i>
                                        </div>
                                    }
                                </div>
                            );
                        })
                    }
                </div>
            );
        }
        else if (colors === "calendars")
        {
            return (
                <div className="po-colorpicker">
                    {
                        CALENDAR_COLORS.map(c =>
                        {
                            return (
                                <div
                                    className="po-colorpicker_color"
                                    style={{
                                        backgroundColor: c.color
                                    }}
                                    onClick={() => this.onColorClick(c)}>
                                    {
                                        c.color === color &&
                                        <div className="po-colorpicker_selected">
                                            <i className="mdi mdi-check"></i>
                                        </div>
                                    }
                                </div>
                            );
                        })
                    }
                </div>
            );
        }
        else if (colors === "status")
        {
            return (
                <div className="po-colorpicker">
                    {
                        this._statusColors.map(c =>
                        {
                            return (
                                <div
                                    className="po-colorpicker_color"
                                    style={{
                                        backgroundColor: c.color
                                    }}
                                    onClick={() => this.onColorClick(c)}>
                                    {
                                        c.color === color &&
                                        <div className="po-colorpicker_selected">
                                            <i className="mdi mdi-check"></i>
                                        </div>
                                    }
                                </div>
                            );
                        })
                    }
                </div>
            );
        }
    }

    /**
     * Rendert die Komponente.
     */
    public render(): JSX.Element
    {
        const { style, colors, hint, errorMessage } = this.props;
        const { color } = this.state;
        var colorToDisplay: IColor = this.getColor(color);
        var classNames: string[] = ["colorpicker"];
        var colorBoxStyle: React.CSSProperties = {};

        if (colorToDisplay != null && colorToDisplay.color != null)
        {
            if (colorToDisplay.color.startsWith("#"))
            {
                colorBoxStyle.backgroundColor = colorToDisplay.color;
            }
            else
            {
                classNames.push("colorpicker--" + colorToDisplay.color);
            }
        }

        return (
            <InputContainer style={{
                marginBottom: 10,
                ...style
            }}
            alignLeft>
                {this.getLabel()}
                <PopupProvider
                    id="color-popup"
                    trigger={PopupTrigger.Click}>
                    <div
                        className={classNames.join(" ")}
                        style={colorBoxStyle}></div>
                </PopupProvider>
                {
                    errorMessage &&
                    <InputError style={{
                        marginTop: 5,
                    }}>
                        {errorMessage}
                    </InputError>
                }
                {
                    hint &&
                    <InputHint style={{
                        marginTop: 5,
                    }}>
                        {hint}
                    </InputHint>
                }
                <Popup
                    id="color-popup"
                    align={PopupAlign.targetNodeBottomLeft}>
                    {this.renderColorPopup()}
                </Popup>
            </InputContainer>
        );
    }
}